import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { ChromePicker } from 'react-color';
import { saveAs } from 'file-saver';
import {
  useRecoilValueLoadable
} from 'recoil';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Input,
  Button,
  Popconfirm,
  Spin,
  Modal,
  notification,
  Avatar,
  Tooltip,
  Empty
} from 'antd';
import { CloudDownloadOutlined } from '@ant-design/icons';
import { Comment } from '@ant-design/compatible';
import { PageHeader } from '@ant-design/pro-layout';
import Grocefy from '../../../../assets/images/grocefyLogoAlone.png';
import { profileAtom } from '../../../../atoms/Atoms';
import api from '../../../../api/api';

function Configurations({ title, containerName, fileName }) {
  const { t } = useTranslation();
  const profile = useRecoilValueLoadable(profileAtom);
  const isAdmin =
    profile.contents && profile.contents.roles.includes('SuperAdmin');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSearch, setLastSearch] = useState('');
  const [allSettings, setAllSettings] = useState([]);
  const [settings, setSettings] = useState([]);
  const [newSetting, setNewSetting] = useState({});

  function hexToRgb(hex) {
    const temp = hex.replace('#', '');
    let r = 0;
    let g = 0;
    let b = 0;
    let a = 1;
    if (temp.length === 6) {
      r = parseInt(temp.substring(0, 2), 16);
      g = parseInt(temp.substring(2, 4), 16);
      b = parseInt(temp.substring(4, 6), 16);
    } else {
      a = parseInt(temp.substring(0, 2), 16) / 255;
      r = parseInt(temp.substring(2, 4), 16);
      g = parseInt(temp.substring(4, 6), 16);
      b = parseInt(temp.substring(6, 8), 16);
    }
    return { r, g, b, a };
  }

  function componentToHex(c) {
    const hex = c.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  }

  function rgbToHex(rgb) {
    if (rgb.a < 1) {
      return `#${componentToHex(parseInt((rgb.a * 255).toFixed(), 10))}${componentToHex(rgb.r)}${componentToHex(rgb.g)}${componentToHex(rgb.b)}`;
    }
    return `#${componentToHex(rgb.r)}${componentToHex(rgb.g)}${componentToHex(rgb.b)}`;
  }

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function filterSettings(searchTerm) {
    if (searchTerm?.length > 0) {
      const filtered = _.filter(allSettings,
        (s) => s.key.toUpperCase().includes(searchTerm.toUpperCase()));
      setSettings(filtered);
    } else {
      setSettings(allSettings);
    }
  }

  // Convert a flat JSON object into the array of { key, value } rows the table uses.
  function objectToSettings(obj) {
    return Object.keys(obj).map((key) => {
      const value = obj[key];
      return {
        key,
        value: value === null || value === undefined ? '' : `${value}`
      };
    });
  }

  // Serialize the current rows back into the flat JSON file payload.
  function settingsToText(arr) {
    const obj = {};
    arr.forEach((s) => {
      obj[s.key] = s.value;
    });
    return JSON.stringify(obj, null, 2);
  }

  function getSettings() {
    setLoading(true);
    api
      .get(`utility/file/${containerName}/${fileName}`)
      .then((response) => {
        setLoading(false);
        setDirty(false);
        if (response.data.error) {
          showMessage(response.data.error);
          setHasData(false);
          setAllSettings([]);
        } else if (response.data.data) {
          setAllSettings(objectToSettings(JSON.parse(response.data.data)));
          setHasData(true);
        } else {
          setHasData(false);
          setAllSettings([]);
        }
      })
      .catch((err) => {
        setLoading(false);
        setHasData(false);
        setAllSettings([]);
        showMessage(err.message);
      });
  }

  // The whole file is written at once: serialize the current rows and overwrite
  // the blob. The upload endpoint returns the re-read content, which we use to
  // refresh local state.
  function saveSettings() {
    setLoading(true);
    api
      .post('utility/upload', {
        containerName,
        fileName,
        text: settingsToText(allSettings)
      })
      .then((response) => {
        setLoading(false);
        if (response.data.error) {
          showMessage(response.data.error);
        } else {
          setAllSettings(response.data.data
            ? objectToSettings(JSON.parse(response.data.data))
            : []);
          setHasData(!!response.data.data);
          setDirty(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        showMessage(err.message);
      });
  }

  // Local edits below only mutate state; nothing is persisted until saveSettings.
  function changeSetting(key, value) {
    setAllSettings((prev) =>
      prev.map((s) => (s.key === key ? { ...s, value } : s)));
    setDirty(true);
  }

  function removeAllSettings() {
    setAllSettings([]);
    setDirty(true);
  }

  function deleteSetting(setting) {
    setAllSettings((prev) => _.filter(prev, (s) => s.key !== setting.key));
    setDirty(true);
  }

  function createSetting() {
    const index = allSettings.findIndex((s) => s.key === newSetting.key);
    if (index > -1) {
      showMessage(t('setting_with_key_exists'));
    } else {
      setAllSettings((prev) =>
        [...prev, { key: newSetting.key, value: newSetting.value }]);
      setDirty(true);
      setShowCreateModal(false);
    }
  }

  function exportSettings() {
    let data = '{\n';
    for (let i = 0; i < allSettings.length; i++) {
      data += `"${allSettings[i].key}": "${allSettings[i].value}"`;
      if (i !== allSettings.length - 1) {
        data += ',\n';
      }
    }
    data += '\n}';
    const file = new Blob([data], { type: 'application/json;charset=utf-8' });
    saveAs(file, `${fileName}.json`);
  }

  useEffect(() => {
    setLastSearch('');
    getSettings();
  }, [containerName, fileName]);

  useEffect(() => {
    filterSettings(lastSearch);
  }, [allSettings]);

  useEffect(() => {
    if (showCreateModal) {
      setNewSetting({
        key: '',
        value: ''
      });
    }
  }, [showCreateModal]);

  return (
    <Spin spinning={loading}>
      <PageHeader
        title={title}
        extra={[
          <Button
            hidden={!isAdmin || !hasData}
            key="save"
            size="small"
            type="primary"
            disabled={!dirty}
            loading={loading}
            onClick={() => saveSettings()}
          >
            {t('save')}
          </Button>,
          <Button
            hidden={!isAdmin || !hasData}
            key="create"
            size="small"
            type="primary"
            onClick={() => setShowCreateModal(true)}
          >
            {t('create')}
          </Button>,
          <Button
            hidden={!isAdmin}
            key="reload"
            size="small"
            type="primary"
            onClick={() => getSettings()}
          >
            {t('reload')}
          </Button>,
          <Popconfirm
            hidden={!isAdmin || !hasData}
            key="removeall"
            title={t('remove_all_settings_prompt')}
            okText={t('yes')}
            onConfirm={() => removeAllSettings()}
          >
            <Button
              size="small"
              type="primary"
            >
              {t('remove_all')}
            </Button>
          </Popconfirm>,
          <Button
            icon={<CloudDownloadOutlined />}
            hidden={!isAdmin || !hasData}
            key="export"
            size="small"
            type="primary"
            onClick={() => exportSettings()}
          >
            {t('export')}
          </Button>
        ]}
      />
      {!hasData && !loading && (
        <Empty description={t('no_configuration_file')} />
      )}
      {hasData && (
        <>
          {t('showing')}: {settings?.length}
          <Input
            placeholder={`${t('search')}...`}
            disabled={loading}
            onChange={(obj) => {
              setLastSearch(obj.target.value);
              filterSettings(obj.target.value);
            }}
          />
          <Table
            dataSource={settings}
            rowKey="key"
            pagination={false}
            columns={[
              {
                title: t('key'),
                align: 'center',
                className: 'text-xs',
                render: (row) => (
                  <span>{row.key}</span>
                ),
              },
              {
                title: t('value'),
                align: 'center',
                className: 'text-xs',
                render: (row) => (
                  <div>
                    {row && row.key.toLowerCase().includes('color') && (
                      <div className="flex">
                        <ChromePicker
                          color={hexToRgb(row?.value)}
                          onChangeComplete={(obj) => {
                            changeSetting(row.key, rgbToHex(obj.rgb));
                          }}
                        />
                      </div>
                    )}
                    {row && !row.key.toLowerCase().includes('color') && (
                      <>
                        <Input
                          value={row.value}
                          onChange={(obj) => {
                            changeSetting(row.key, obj.target.value);
                          }}
                        />
                      </>
                    )}
                  </div>
                ),
              },
              {
                title: '',
                align: 'center',
                className: 'text-xs',
                render: (row) => (
                  <Popconfirm
                    title={t('delete_setting_prompt')}
                    okText={t('yes')}
                    cancelText="No"
                    onConfirm={() => {
                      deleteSetting(row);
                    }}
                  >
                    <Button
                      type="primary"
                      danger
                      size="small"
                      className="m-1"
                    >
                      {t('remove')}
                    </Button>
                  </Popconfirm>
                ),
              },
            ]}
          />
        </>
      )}
      <Modal
        title={t('create_setting')}
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            danger
            loading={loading}
            onClick={() => setShowCreateModal(false)}
          >
            {t('close')}
          </Button>,
          <Button
            key="ok"
            type="primary"
            disabled={!newSetting.key ||
                !newSetting.value ||
                !(newSetting.key?.length > 0) ||
                !(newSetting.value?.length > 0)}
            loading={loading}
            onClick={() => createSetting()}
          >
            {t('create')}
          </Button>
        ]}
      >
        <div className="mb-2">
          <strong>{t('key')}</strong>
          <Input
            disabled={loading}
            value={newSetting?.key}
            onChange={(obj) => {
              setNewSetting({
                ...newSetting,
                key: obj.target.value.trim()
              });
            }}
          />
        </div>
        <div className="mb-2">
          <strong>{t('value')}</strong>
          <Input
            disabled={loading}
            value={newSetting?.value}
            onChange={(obj) => {
              setNewSetting({
                ...newSetting,
                value: obj.target.value.trim()
              });
            }}
          />
        </div>
      </Modal>
    </Spin>
  );
}

export default Configurations;
